'use client';

import { useEffect, useRef } from 'react';

/**
 * Renders a widget exactly as a partner's site would: by injecting the real
 * `kalshi-embed-event.js` loader with the given inline JSON config. The loader
 * builds the iframe -> /external-widgets/* (or /banner), so the preview is the
 * embed. The chart/data still come from the main app via that route.
 *
 * `autoHeight` fits the frame to the embedded widget's real content height
 * (same-origin preview only) so a short widget (e.g. a 2-row watchlist) doesn't
 * leave empty space below it.
 */
export function EmbedScriptFrame({
    config,
    inline = false,
    autoHeight = false,
}: {
    config: Record<string, unknown>;
    inline?: boolean;
    autoHeight?: boolean;
}) {
    const containerRef = useRef<HTMLDivElement>(null);
    const serialized = JSON.stringify(config);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const clear = () => {
            container.querySelectorAll('script').forEach((el) => el.remove());
            const widget = container.querySelector('.kalshi-embed-container__widget') as HTMLDivElement | null;
            if (widget) widget.innerHTML = '';
        };

        clear();
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = `${window.location.origin}/js/kalshi-embed-event.js`;
        script.textContent = `\n${JSON.stringify(JSON.parse(serialized), null, 2)}\n`;
        container.appendChild(script);

        if (!autoHeight) return clear;

        // Fit the iframe to the embedded widget's real content height so a short
        // widget doesn't leave empty space below it. Same-origin preview only.
        let iframe: HTMLIFrameElement | null = null;
        const fit = () => {
            if (!iframe) return;
            try {
                const doc = iframe.contentWindow?.document;
                if (!doc) return;
                // Collapse first so scrollHeight reflects the content's natural height
                // rather than the iframe viewport: the embed sets html,body{height:100%}
                // (and the loader defaults % heights to 360px), so a plain read would just
                // echo the current iframe height. The 0px isn't painted -- it only forces
                // the layout read of the content's real overflow height.
                iframe.style.height = '0px';
                const h = doc.documentElement.scrollHeight;
                if (h) iframe.style.height = `${h}px`;
            } catch {
                /* cross-origin (shouldn't happen for a same-origin preview) */
            }
        };
        const observer = new MutationObserver(() => {
            const found = container.querySelector('iframe');
            if (found && found !== iframe) {
                iframe = found;
                iframe.addEventListener('load', fit);
            }
            fit();
        });
        observer.observe(container, { childList: true, subtree: true });
        const interval = window.setInterval(fit, 400);

        return () => {
            observer.disconnect();
            window.clearInterval(interval);
            iframe?.removeEventListener('load', fit);
            clear();
        };
    }, [serialized, autoHeight]);

    return (
        <div
            ref={containerRef}
            className={
                inline
                    ? 'kalshi-embed-container'
                    : autoHeight
                      ? 'kalshi-embed-container w-full'
                      : 'kalshi-embed-container h-full w-full'
            }
            style={inline ? { display: 'inline-block', overflow: 'visible' } : { overflow: 'visible' }}
        >
            <div
                className="kalshi-embed-container__widget"
                style={inline ? { display: 'inline-block' } : { width: '100%', height: autoHeight ? 'auto' : '100%' }}
            />
        </div>
    );
}
